import React, {useEffect, useState} from 'react';
import {
  Alert,
  FlatList,
  Image,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import HomeInput from '../../../../components/HomeInput/HomeInput';
import {Colors} from '../../../../constants/Colors';
import CompanyCard from './components/CompanyCard/CompanyCard';
import {styles} from './styles';
import {useBagStore} from '../../../../store/BagStore';
import {useFilterStore} from '../../../../store/FilterStore';
import AuthService from '../../../../services/AuthService/AuthService';
import {useUserStore} from '../../../../store/UserStore';
import {useAuthStore} from '../../../../store/AuthStore';
import {useCompanyStore} from '../../../../store/CompanyStore';
import {useCatalogStore} from '../../../../store/CatalogStore';
import {useNavigation, useRoute} from '@react-navigation/native';
import {NavigationProps} from '../../../../navigation/Routes';
import {AppRoutes} from '../../../../navigation/Routes.types';
import {ActivityIndicator} from 'react-native';
import ButtonComponent from '../../../../components/ButtonComponent/ButtonComponent';
import {useModalStore} from '../../../../store/MenuModalStore';
import {Images} from '../../../../constants/Images';
import Input from '../../../../components/Input/Input';
import {KeyboardAvoidingView} from 'react-native';
import {set} from 'zod';
import {getAnalytics, logEvent} from '@react-native-firebase/analytics';

interface IModalFilter {
  visible: boolean;
  onRequestClose: () => void;
  onPress: () => void;
  hasOrder?: boolean;
  hasCategory?: boolean;
  hasBrand?: boolean;
  hasNews?: boolean;
  hasSize?: boolean;
  hasNewCampaigns?: boolean;
  HasOngoingCampaigns?: boolean;
  hasInput?: boolean;
}

interface ICompany {
  id: string;
  name: string;
  clientCode: string;
  cnpj_cpf: string;
  subchannel: string;
  user_id: number;
  ultimoPedido: string | null;
  address: {
    id: number;
    client_id: number;
    street: string;
    neighborhood: string;
    city: string;
    state: string;
    zip_code: string;
    created_at: string;
    updated_at: string;
  };
}

const CompanyModal = ({
  visible,
  onRequestClose,
  hasInput,
  onPress,
}: IModalFilter) => {
  const [companies, setCompanies] = React.useState<ICompany[]>([]);
  const [allCompanies, setAllCompanies] = React.useState<ICompany[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const {user, userType, networkAccountId, setUser, setCatalogBlocked, setTemporaryPasswordLogin} = useUserStore();
  const {token, setToken, defUser} = useAuthStore();
  const {clearCart} = useBagStore();
  const {clearCatalog} = useCatalogStore();
  const {clearFilters} = useFilterStore();
  const [loading, setLoading] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);
  const {setCompanies: setCompaniesStore} = useCompanyStore();
  const [showCreateRequest, setShowCreateRequest] = React.useState(false);
  const {navigate} = useNavigation<NavigationProps>();
  const [groupRequests, setGroupRequests] = React.useState<any[]>([]);
  const [loadingGroupRequests, setLoadingGroupRequests] = React.useState(false);
  const [showRequests, setShowRequests] = React.useState(false);
  const {closeModal} = useModalStore();
  const getCompanies = async () => {
    if (!user) {
      return;
    }
    try {
      setLoading(true);
      const response = await AuthService.getMyCompaniesForUserType(
        token,
        userType || 'client',
        networkAccountId,
        userType === 'client' ? (user as any).clientCode : undefined,
      );
      setAllCompanies(response);
      setCompanies(response);
      setCompaniesStore(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getGroupRequests = async () => {
    if (!user || userType === 'network') {
      return;
    }
    try {
      setLoadingGroupRequests(true);
      const response = await AuthService.getGroupRequests(
        token,
        user.clientCode,
      );
      if (response && response.length > 0) {
        setGroupRequests(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingGroupRequests(false);
    }
  };

  React.useEffect(() => {
    if (visible) {
      (async () => {
        await getCompanies();
        await getGroupRequests();
      })();
    }
  }, [visible]);

  const handleClientCodeUpdate = async (
    clientCode: string,
    cpfCnpj: string,
  ) => {
    if (!user) {
      return;
    }

    try {
      setSwitching(true);

      // Always call backend to change the active company. The backend now supports
      // both regular Users and NetworkAccount principals and will return the
      // proper user object and token for both cases.
      const regularUser = user as any;

      if (cpfCnpj !== regularUser?.current_client?.cnpj_cpf) {
        const response = await AuthService.changeAuthByCompany(
          token,
          regularUser.clientCode,
          clientCode,
        );

        // Update local stores with server response
        await Promise.all([
          setUser(response.user),
          setToken(response.access_token),
        ]);

        setCatalogBlocked(response?.catalog_blocked ?? false, response?.catalog_blocked_message);
        setTemporaryPasswordLogin(response?.user?.temporary_password_login ?? false);

        // If the current principal is a network account, persist via defUser as well
        if (userType === 'network') {
          defUser(response.user);
        }

        // Only clear cart and catalog when actually switching to a different company
        clearCart();
        clearCatalog();
      }

      // UI flows common for both user types
      onRequestClose();
      onPress();
      // Navigate to main tab and open Home tab
      (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, {screen: 'Home'});
      closeModal();
    } catch (error) {
      console.error('Error switching company:', error);
    } finally {
      setSwitching(false);
    }
  };
  const analyticsInstance = getAnalytics();
  const filterByCompanyName = (text: string) => {
    setSearchText(text);

    if (!text) {
      setCompanies(allCompanies);
      return;
    }

    const filtered = allCompanies.filter(company =>
      company.name.toLowerCase().includes(text.toLowerCase()),
    );

    setCompanies(filtered);
  };
  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onRequestClose}>
      <View style={styles.containerModal}>
        <View style={styles.containerFilter}>
          <View style={styles.spaceHorizontal}>
            <TouchableOpacity onPress={onRequestClose}>
              <Feather
                name="x"
                size={32}
                color={Colors.GrayPallete.lightgray}
                style={styles.iconX}
              />
            </TouchableOpacity>
            <Text style={styles.filterText}>
              {showRequests
                ? 'Solicitações de inclusão'
                : 'Selecione uma empresa'}
            </Text>
            {hasInput && !showRequests && (
              <View style={styles.containerInput}>
                <HomeInput
                  placeholder="O que você procura?"
                  onPress={() => null}
                  value={searchText}
                  onChangeText={text => filterByCompanyName(text)}
                />
              </View>
            )}
            {loading && (
              <ActivityIndicator size="large" color={Colors.RedPallete.red} />
            )}
            {!loading && !showRequests && (
              <FlatList
                style={{ flex: 1 }}
                data={companies}
                keyExtractor={item => item.id}
                renderItem={({item}) => (
                  <CompanyCard
                    key={item.id}
                    company={item}
                    onClientCodeUpdate={handleClientCodeUpdate}
                    actualClient={
                      (user as any)?.current_client?.cnpj_cpf === item.cnpj_cpf
                    }
                    current_cnpj_cpf={(user as any)?.current_client?.cnpj_cpf}
                  />
                )}
                ListFooterComponent={() => (
                  <View style={styles.containerButtons}>
                    <Text style={styles.label}>
                      Não encontrou a empresa que procura?
                    </Text>
                    {(user as any)?.group ? (
                      <ButtonComponent
                        title="Solicite a inclusão"
                        onPress={() => {
                          onRequestClose();
                          closeModal();
                          setTimeout(() => {
                            navigate(AppRoutes.REQUEST_COMPANY);
                          }, 300);
                        }}
                      />
                    ) : (
                      <ButtonComponent
                        title="Solicite um grupo"
                        onPress={() => {
                          logEvent(analyticsInstance, 'request_company_group', {
                            client_code: (user as any)?.clientCode,
                          });
                          onRequestClose();
                          closeModal();
                          setTimeout(() => {
                            navigate(AppRoutes.REQUEST_COMPANY);
                          }, 300);
                        }}
                      />
                    )}
                    {groupRequests && groupRequests.length > 0 && (
                      <View style={{marginTop: 10}}>
                        <ButtonComponent
                          title="Acompanhe solicitações"
                          onPress={() => {
                            logEvent(analyticsInstance, 'view_group_requests', {
                              client_code: (user as any)?.clientCode,
                            });
                            setShowRequests(!showRequests);
                          }}
                          outlined
                        />
                      </View>
                    )}
                  </View>
                )}
              />
            )}
            {!loading && showRequests && (
              <FlatList
                style={{
                  flex: 1,
                  marginBottom: 80,
                }}
                data={groupRequests}
                keyExtractor={(item, index) => index.toString()}
                renderItem={({item}) => (
                  <View style={styles.requestContainer}>
                    <View style={styles.requestItemContainer}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 10,
                        }}>
                        <Text style={styles.requestText}>Nome do grupo:</Text>
                        <Text style={styles.requestStatus}>
                          {item?.group_name}
                        </Text>
                      </View>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 10,
                        }}>
                        <Text style={styles.requestText}>Status:</Text>
                        <Text style={styles.requestStatus}>
                          {item?.request_status === 'canceled'
                            ? 'Cancelado'
                            : item?.request_status === 'concluded'
                            ? 'Concluído'
                            : 'Processando'}
                        </Text>
                        <Feather
                          name={
                            item?.request_status === 'canceled'
                              ? 'x-circle'
                              : item?.request_status === 'concluded'
                              ? 'check-circle'
                              : 'clock'
                          }
                          size={20}
                          color={
                            item?.request_status === 'canceled'
                              ? Colors.RedPallete.red
                              : item?.request_status === 'concluded'
                              ? Colors.GreenPallete.greenOne
                              : '#FFA500'
                          }
                          style={styles.statusIcon}
                        />
                      </View>
                      {item?.notes && (
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 10,
                          }}>
                          <Text style={styles.requestText}>Retorno:</Text>
                          <Text style={styles.requestedDocument}>
                            {item?.notes}
                          </Text>
                        </View>
                      )}
                      <Text style={styles.requestText}>
                        Documentos Solicitados:
                      </Text>
                      {item?.requested_documents.map((doc, idx) => (
                        <Text style={styles.requestedDocument} key={idx}>
                          {doc.requested_document}
                        </Text>
                      ))}
                    </View>
                  </View>
                )}
                ListHeaderComponent={() => (
                  <View style={styles.containerButtonRequest}>
                    <ButtonComponent
                      title="Voltar"
                      onPress={() => setShowRequests(!showRequests)}
                      outlined
                    />
                  </View>
                )}
              />
            )}
          </View>
        </View>
      </View>
      {switching && (
        <View style={switchingStyles.overlay}>
          <Image source={Images.bottleLoading} style={switchingStyles.bottle} />
          <Text style={switchingStyles.text}>Carregando</Text>
          <ActivityIndicator size="small" color={Colors.RedPallete.red} />
        </View>
      )}
    </Modal>
  );
};

const switchingStyles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottle: {
    marginBottom: 20,
  },
  text: {
    fontSize: 18,
    fontFamily: 'TCCC-UnityText Bold',
    color: '#e60000',
    marginVertical: 50,
  },
});

export default CompanyModal;
